import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { formatDisplayText } from '../../utils/text-case.utils';
import type { TextCaseMode } from '../../utils/text-case.utils';

interface TextWidgetFontConfig {
  size?: string | number;
  weight?: string | number;
  style?: 'normal' | 'italic' | 'oblique';
  family?: string;
  color?: string;
}

interface TextWidgetIconStyleConfig {
  size?: string | number;
  iconSize?: string | number;
  borderRadius?: string | number;
  backgroundColor?: string;
  color?: string;
}

interface NavigationButtonStyleConfig {
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
  borderRadius?: string | number;
  borderWidth?: string | number;
  boxShadow?: string;
  padding?: string;
  height?: string | number;
  minHeight?: string | number;
  minWidth?: string | number;
  width?: string | number;
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  font?: TextWidgetFontConfig;
}

interface TextWidgetNavigationButton {
  label?: string;
  text?: string;
  content?: string;
  route?: string;
  navigateTo?: string;
  path?: string;
  url?: string;
  navigateAction?: Record<string, any>;
  disabled?: boolean;
  buttonStyle?: NavigationButtonStyleConfig;
}

interface TextWidgetNavigationOption {
  label?: string;
  text?: string;
  content?: string;
  moduleLabel?: string;
  subtitle?: string;
  topIcon?: string;
  bottomIcon?: string;
  route?: string;
  navigateTo?: string;
  path?: string;
  url?: string;
  navigateAction?: Record<string, any>;
  disabled?: boolean;
  buttonStyle?: NavigationButtonStyleConfig;
  buttons?: TextWidgetNavigationButton[];
}

interface TextWidgetNavigationNConfig {
  text?: string;
  content?: string;
  label?: string;
  title?: string;
  moduleLabel?: string;
  subtitle?: string;
  topIcon?: string;
  bottomIcon?: string;
  navigateTo?: string;
  navigateAction?: Record<string, any>;
  textCase?: TextCaseMode;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: string | number;
  letterSpacing?: string | number;
  wrap?: boolean;
  maxLines?: number;
  textOverflow?: 'clip' | 'ellipsis';
  font?: TextWidgetFontConfig;
  showIndicator?: boolean;
  openIndicator?: string;
  closeIndicator?: string;
  expandOnInit?: boolean;
  closeOnSelect?: boolean;
  closeOnOutsideClick?: boolean;
  buttonsPerRow?: number;
  modulesPerRow?: number;
  moduleWidth?: string | number;
  moduleHeight?: string | number;
  moduleMinWidth?: string | number;
  moduleJustify?: 'stretch' | 'start' | 'center' | 'end';
  optionsGap?: string | number;
  bottomGap?: string | number;
  maxWidth?: string | number;
  cardPadding?: string;
  topBorderHeight?: string | number;
  topBorderColor?: string;
  dividerColor?: string;
  headerGap?: string | number;
  actionButtonsGap?: string | number;
  moduleLabelFont?: TextWidgetFontConfig;
  titleFont?: TextWidgetFontConfig;
  subtitleFont?: TextWidgetFontConfig;
  iconStyle?: TextWidgetIconStyleConfig;
  triggerBoxStyle?: NavigationButtonStyleConfig;
  optionButtonStyle?: NavigationButtonStyleConfig;
  actionButtonStyle?: NavigationButtonStyleConfig;
  options?: TextWidgetNavigationOption[];
}

@Component({
  selector: 'app-text-widget-navigation-n',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './text-widget-navigation-n.component.html',
  styleUrl: './text-widget-navigation-n.component.scss'
})
export class TextWidgetNavigationNComponent {
  private readonly router = inject(Router);

  private config?: TextWidgetNavigationNConfig;

  @Input() set data(value: TextWidgetNavigationNConfig | undefined) {
    this.config = value;
  }

  get data(): TextWidgetNavigationNConfig | undefined {
    return this.config;
  }

  get textCase(): TextCaseMode {
    return (this.data?.textCase || 'none') as TextCaseMode;
  }

  get navigationOptions(): TextWidgetNavigationOption[] {
    return Array.isArray(this.data?.options) ? this.data.options : [];
  }

  get hasOptions(): boolean {
    return this.navigationOptions.length > 0;
  }

  get color(): string {
    return this.data?.color || 'var(--dvm-content-text, #111827)';
  }

  get buttonsGridTemplateColumns(): string {
    const count = Number(this.data?.modulesPerRow ?? this.data?.buttonsPerRow);
    const optionCount = this.navigationOptions.length || 1;
    const columnCount = Number.isFinite(count) && count > 0 ? Math.min(count, optionCount) : optionCount;
    return columnCount > 0
      ? `repeat(${columnCount}, minmax(0, 1fr))`
      : 'repeat(auto-fit, minmax(140px, 1fr))';
  }

  get optionsGap(): string {
    return this.withDefaultUnit(this.data?.optionsGap, '8px');
  }

  get bottomGap(): string {
    return this.withDefaultUnit(this.data?.bottomGap, '0');
  }

  get panelMaxWidth(): string {
    return this.withDefaultUnit(this.data?.maxWidth, '100%');
  }

  get cardPadding(): string {
    return this.data?.cardPadding || '44px 56px 48px';
  }

  get topBorderHeight(): string {
    return this.withDefaultUnit(this.data?.topBorderHeight, '8px');
  }

  getTopBorderColor(option: TextWidgetNavigationOption): string {
    return this.data?.topBorderColor || this.getAccentColor(option);
  }

  getDividerColor(option: TextWidgetNavigationOption): string {
    return this.data?.dividerColor || this.getAccentColor(option);
  }

  get headerGap(): string {
    return this.withDefaultUnit(this.data?.headerGap, '14px');
  }

  get actionButtonsGap(): string {
    return this.withDefaultUnit(this.data?.actionButtonsGap, '12px');
  }

  get moduleJustify(): string {
    return this.data?.moduleJustify || (this.data?.moduleWidth ? 'center' : 'stretch');
  }

  onOptionClick(option: TextWidgetNavigationOption, button: TextWidgetNavigationButton, event: Event): void {
    event.stopPropagation();
    if (option.disabled || button.disabled) {
      return;
    }

    const navigated = executeNavigationFromConfig(
      this.router,
      this.buildNavigationData(option, button),
      {
        name: this.getButtonLabel(button),
        value: button.navigateTo ?? button.route ?? button.path ?? button.url,
      }
    );

    void navigated;
  }

  onLegacyOptionClick(option: TextWidgetNavigationOption, event: Event): void {
    this.onOptionClick(option, option, event);
  }

  formatOptionLabel(option: TextWidgetNavigationOption): string {
    return formatDisplayText(this.getOptionLabel(option), this.textCase);
  }

  formatButtonLabel(button: TextWidgetNavigationButton): string {
    return formatDisplayText(this.getButtonLabel(button), this.textCase);
  }

  getOptionModuleLabel(option: TextWidgetNavigationOption): string {
    return formatDisplayText(option.moduleLabel, this.textCase);
  }

  getOptionSubtitle(option: TextWidgetNavigationOption): string {
    return String(option.subtitle ?? '');
  }

  getOptionTopIcon(option: TextWidgetNavigationOption): string {
    return String(option.topIcon ?? '');
  }

  getOptionBottomIcon(option: TextWidgetNavigationOption): string {
    return String(option.bottomIcon ?? '');
  }

  getIconWrapSize(): string {
    return this.withDefaultUnit(this.data?.iconStyle?.size, '46px');
  }

  getIconSize(): string {
    return this.withDefaultUnit(this.data?.iconStyle?.iconSize, '24px');
  }

  getIconBorderRadius(): string {
    return this.withDefaultUnit(this.data?.iconStyle?.borderRadius, '12px');
  }

  getIconBackgroundColor(option: TextWidgetNavigationOption): string {
    return this.data?.iconStyle?.backgroundColor || this.getOptionBackgroundColor(option);
  }

  getIconColor(option: TextWidgetNavigationOption): string {
    return this.data?.iconStyle?.color || this.getAccentColor(option);
  }

  getModuleLabelFontSize(): string {
    return this.withDefaultUnit(this.data?.moduleLabelFont?.size, '12px');
  }

  getModuleLabelFontWeight(): string {
    return String(this.data?.moduleLabelFont?.weight ?? 800);
  }

  getModuleLabelColor(option: TextWidgetNavigationOption): string {
    return this.data?.moduleLabelFont?.color || this.getAccentColor(option);
  }

  getTitleFontSize(): string {
    return this.withDefaultUnit(this.data?.titleFont?.size, '20px');
  }

  getTitleFontWeight(): string {
    return String(this.data?.titleFont?.weight ?? 800);
  }

  getTitleColor(option: TextWidgetNavigationOption): string {
    return this.data?.titleFont?.color || this.getOptionTextColor(option);
  }

  getSubtitleFontSize(): string {
    return this.withDefaultUnit(this.data?.subtitleFont?.size, '13px');
  }

  getSubtitleFontWeight(): string {
    return String(this.data?.subtitleFont?.weight ?? 400);
  }

  getSubtitleColor(option: TextWidgetNavigationOption): string {
    return this.data?.subtitleFont?.color || this.getOptionTextColor(option);
  }

  getActionButtons(option: TextWidgetNavigationOption): TextWidgetNavigationButton[] {
    return Array.isArray(option.buttons) && option.buttons.length > 0
      ? option.buttons
      : [option];
  }

  hasActionButtons(option: TextWidgetNavigationOption): boolean {
    return Array.isArray(option.buttons) && option.buttons.length > 0;
  }

  isImageIcon(icon: string | undefined | null): boolean {
    const value = String(icon || '').trim().toLowerCase();
    return value.includes('/') || value.endsWith('.svg') || value.endsWith('.png') || value.endsWith('.jpg') || value.endsWith('.jpeg') || value.endsWith('.webp');
  }

  getOptionBackgroundColor(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.backgroundColor ||
      this.data?.triggerBoxStyle?.backgroundColor ||
      this.data?.optionButtonStyle?.backgroundColor ||
      'var(--dvm-card-bg, #ffffff)'
    );
  }

  getOptionTextColor(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.color ||
      this.data?.triggerBoxStyle?.color ||
      this.data?.optionButtonStyle?.color ||
      this.color
    );
  }

  getOptionBorderColor(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.borderColor ||
      this.data?.triggerBoxStyle?.borderColor ||
      this.data?.optionButtonStyle?.borderColor ||
      'var(--dvm-filter-border-color, #d1d5db)'
    );
  }

  getOptionBorderWidth(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.borderWidth ??
      this.data?.triggerBoxStyle?.borderWidth ??
      this.data?.optionButtonStyle?.borderWidth,
      '1px'
    );
  }

  getOptionBorderRadius(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.borderRadius ??
      this.data?.triggerBoxStyle?.borderRadius ??
      this.data?.optionButtonStyle?.borderRadius,
      '8px'
    );
  }

  getOptionBoxShadow(option: TextWidgetNavigationOption): string {
    return String(
      option.buttonStyle?.boxShadow ||
      this.data?.triggerBoxStyle?.boxShadow ||
      this.data?.optionButtonStyle?.boxShadow ||
      'none'
    );
  }

  getOptionPadding(option: TextWidgetNavigationOption): string {
    return String(
      option.buttonStyle?.padding ||
      this.data?.triggerBoxStyle?.padding ||
      this.data?.optionButtonStyle?.padding ||
      '0'
    );
  }

  getOptionMinHeight(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.minHeight ??
      this.data?.triggerBoxStyle?.minHeight ??
      this.data?.optionButtonStyle?.minHeight,
      '48px'
    );
  }

  getOptionHeight(option: TextWidgetNavigationOption): string | null {
    const value =
      option.buttonStyle?.height ??
      this.data?.triggerBoxStyle?.height ??
      this.data?.optionButtonStyle?.height ??
      this.data?.moduleHeight;
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return this.withDefaultUnit(value, 'auto');
  }

  getOptionMinWidth(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.minWidth ??
      this.data?.triggerBoxStyle?.minWidth ??
      this.data?.optionButtonStyle?.minWidth ??
      this.data?.moduleMinWidth,
      '0'
    );
  }

  getOptionWidth(option: TextWidgetNavigationOption): string | null {
    const value =
      option.buttonStyle?.width ??
      this.data?.triggerBoxStyle?.width ??
      this.data?.optionButtonStyle?.width ??
      this.data?.moduleWidth;
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return this.withDefaultUnit(value, 'auto');
  }

  getOptionFontSize(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.font?.size ??
      this.data?.triggerBoxStyle?.font?.size ??
      this.data?.optionButtonStyle?.font?.size,
      this.withDefaultUnit(this.data?.font?.size, '14px')
    );
  }

  getOptionFontWeight(option: TextWidgetNavigationOption): string {
    return String(
      option.buttonStyle?.font?.weight ??
      this.data?.triggerBoxStyle?.font?.weight ??
      this.data?.optionButtonStyle?.font?.weight ??
      this.data?.font?.weight ??
      400
    );
  }

  getOptionFontStyle(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.font?.style ||
      this.data?.triggerBoxStyle?.font?.style ||
      this.data?.optionButtonStyle?.font?.style ||
      this.data?.font?.style ||
      'normal'
    );
  }

  getOptionFontFamily(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.font?.family ||
      this.data?.triggerBoxStyle?.font?.family ||
      this.data?.optionButtonStyle?.font?.family ||
      this.data?.font?.family ||
      'inherit'
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  getActionButtonBackgroundColor(button: TextWidgetNavigationButton): string {
    return (
      button.buttonStyle?.backgroundColor ||
      this.data?.actionButtonStyle?.backgroundColor ||
      this.getOptionBackgroundColor({} as TextWidgetNavigationOption)
    );
  }

  getActionButtonTextColor(button: TextWidgetNavigationButton): string {
    return button.buttonStyle?.color || this.data?.actionButtonStyle?.color || this.color;
  }

  getActionButtonBorderRadius(button: TextWidgetNavigationButton): string {
    return this.withDefaultUnit(
      button.buttonStyle?.borderRadius ?? this.data?.actionButtonStyle?.borderRadius,
      '16px'
    );
  }

  getActionButtonMinHeight(button: TextWidgetNavigationButton): string {
    return this.withDefaultUnit(
      button.buttonStyle?.minHeight ?? this.data?.actionButtonStyle?.minHeight,
      '40px'
    );
  }

  getActionButtonFontSize(button: TextWidgetNavigationButton): string {
    return this.withDefaultUnit(
      button.buttonStyle?.font?.size ?? this.data?.actionButtonStyle?.font?.size,
      '14px'
    );
  }

  getActionButtonFontWeight(button: TextWidgetNavigationButton): string {
    return String(button.buttonStyle?.font?.weight ?? this.data?.actionButtonStyle?.font?.weight ?? 800);
  }

  getActionButtonPadding(button: TextWidgetNavigationButton): string {
    return button.buttonStyle?.padding || this.data?.actionButtonStyle?.padding || '8px 14px';
  }

  private getAccentColor(option: TextWidgetNavigationOption): string {
    return this.data?.actionButtonStyle?.color || this.getOptionTextColor(option);
  }

  private getOptionLabel(option: TextWidgetNavigationOption): string {
    return String(option.label ?? option.text ?? option.content ?? '');
  }

  private getButtonLabel(button: TextWidgetNavigationButton): string {
    return String(button.label ?? button.text ?? button.content ?? '');
  }

  private buildNavigationData(option: TextWidgetNavigationOption, button: TextWidgetNavigationButton): Record<string, any> {
    const optionRoute = option.navigateTo ?? option.route ?? option.path ?? option.url;
    const buttonRoute = button.navigateTo ?? button.route ?? button.path ?? button.url;
    const route = buttonRoute ?? optionRoute;
    const parentNavigateAction = this.data?.navigateAction || {};
    const mergedNavigateAction = {
      ...parentNavigateAction,
      ...(option.navigateAction || {}),
      ...(button.navigateAction || {}),
    };
    const optionNavigateAction = Object.keys(mergedNavigateAction).length > 0
      ? mergedNavigateAction
      : route
        ? { ...parentNavigateAction, route }
        : this.data?.navigateAction;

    return {
      ...this.data,
      ...option,
      ...button,
      moduleLabel: option.moduleLabel,
      navigateTo: route ?? this.data?.navigateTo,
      navigateAction: optionNavigateAction,
    };
  }

  private withDefaultUnit(value: string | number | undefined, fallback: string): string {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }
    if (typeof value === 'number') {
      return `${value}px`;
    }
    return String(value);
  }

}
